import type { Styles } from '@adornis/ass/style.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import { Bundesland } from '@chancenwerk/enums/enums.js';
import { ChancenDynamicClassProduct } from '@chancenwerk/shared/_cms/products/entities/ChancenDynamicClassProduct.js';
import type { Product } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import type { ZipCity } from '@chancenwerk/shared/adresse.js';
import type { KpfFilter } from '@chancenwerk/shared/filter/KpfFilter.js';
import { BundeslandAspect } from '@chancenwerk/shared/master-data-management/aspects/BundeslandAspect.js';
import { ZipCityAspect } from '@chancenwerk/shared/master-data-management/aspects/ZipCityAspect.js';
import { ProductAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/ProductAspect.js';
import type { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { debounceTime, switchMap, takeUntil } from 'rxjs';
import { globalKpfFilter } from '../globalSubjects.js';

@customElement('kpf-filter-btn')
export class KpfFilterBtn extends ChemistryLitElement {
  @property({ attribute: false }) public label;
  @property({ attribute: false }) public value;

  override styles = () => [
    { ':host': { background: '#D8DADE', color: '#333', padding: '6px 10px', borderRadius: '6px' } },
  ];

  private readonly _valueLabelStyle: Styles = {
    fontSize: '13px',
    fontWeight: '600',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '100px',
  };

  override render() {
    return html`
      <x-flex horizontal space="xs">
        <x-text ${css({ fontSize: '13px', color: '#7D7D7D' })}>${this.label}:</x-text>
        <x-text ${css(this._valueLabelStyle)}>${this.value}</x-text>
      </x-flex>
    `;
  }
}

@customElement('board-component-kpf-filter')
export class BoardComponentKpfFiltera extends ChemistryLitElement {
  @state() private readonly _productClass = new RXController(this, '');

  @state() private readonly _productName = new RXController(
    this,
    this._productClass.observable.pipe(
      switchMap(value =>
        ChancenDynamicClassProduct.getChancenDynamicClassProductByClassName(value)({ name: 1, kuerzel: 1 }),
      ),
    ),
  );

  @property() public name = new RXController<Renderable>(this, 'Filter');
  @property({ attribute: false }) public globalFilter = new RXController(this, globalKpfFilter);
  @property({ attribute: false }) public kpfsPaginated: SubscriptionPagination<
    KPF,
    Array<KpfFilter | undefined>
  > | null = null;

  override render() {
    return html`${this._getFilterKpfSection()}`;
  }

  override connectedCallback(): void {
    super.connectedCallback();

    this.globalFilter.observable.pipe(debounceTime(500), takeUntil(this.disconnected)).subscribe(filter => {
      if (this.kpfsPaginated !== null) {
        this.kpfsPaginated.updateParameters(filter);
      }
    });
  }

  get filters() {
    const res = [
      ['search', 'Kürzel | Schule'],
      ['productName', 'Produkt'],
      ['state', 'Bundesland'],
      ['zipCity', 'Stadt'],
    ].map(([key, label]) => {
      if (key === 'productName') {
        return { key, label, value: this._productName.value };
      } else {
        return { key, label, value: this.globalFilter.value[key]?.toString() as string };
      }
    });
    return res;
  }

  private _getFilterKpfSection() {
    return html`
      <cr-card ${css({ width: '100%' })} space="md" padding="md">
        <slot name="front"></slot>
        <cr-nospace-toggle-content
          .label=${html`
            <x-text>${this.name.value}</x-text>
            <x-flex ${css({ flexWrap: 'wrap' })} horizontal space="xs">
              ${this.filters.map(filter =>
                filter.value
                  ? html`<kpf-filter-btn .label=${filter.label} .value=${filter.value}></kpf-filter-btn>`
                  : '',
              )}
            </x-flex>
          `}
        >
          <cr-flex space="md" ${css({ maxWidth: '720px' })}>
            <cr-input
              placeholder="Kürzel | Schule"
              icon="search"
              .value=${this.globalFilter.value.search}
              @value-changed=${(e: CustomEvent) => {
                if (this.globalFilter.value.search !== e.detail.value) {
                  this.globalFilter.value.search = e.detail.value;
                  this.globalFilter.next(this.globalFilter.value);
                }
              }}
            >
            </cr-input>
            <chancen-component-semantic-product-picker
              @value-picked=${(e: ValueEvent<Maybe<Product>>) => {
                this.globalFilter.value.setFilterAspect(
                  new ProductAspect({ fieldName: 'produkt._class', values: [e.detail.value?._class ?? ''] }),
                );
                this._productClass.next(e.detail.value?._class ?? '');
                this.globalFilter.next(this.globalFilter.value);
              }}
            >
            </chancen-component-semantic-product-picker>

            <chancen-component-zip-city-picker
              flex
              only-cities
              ${css({ minWidth: '200px' })}
              clearable
              .value=${this.globalFilter.value.getFilterAspect<ZipCityAspect>('zipCityOfPartner')?.zipCity}
              @value-picked=${(e: CustomEvent<{ value: ZipCity }>) => {
                this.globalFilter.value.setFilterAspect(
                  new ZipCityAspect({ customID: 'zipCityOfPartner', zipCity: e.detail.value }),
                );
                this.globalFilter.next(this.globalFilter.value);
              }}
            >
            </chancen-component-zip-city-picker>

            <cr-dropdown-selection
              clearable
              icon="search"
              placeholder="Bundesland"
              .value=${this.globalFilter.value.getFilterAspect<BundeslandAspect>('state')?.value}
              .selectables=${Object.values(Bundesland)}
              .renderString=${(entry: Bundesland) => entry}
              @value-picked=${(e: CustomEvent<{ value: Bundesland }>) => {
                this.globalFilter.value.setFilterAspect(
                  new BundeslandAspect({ customID: 'state', value: e.detail.value }),
                );

                this.globalFilter.next(this.globalFilter.value);
              }}
            ></cr-dropdown-selection>
          </cr-flex>
        </cr-nospace-toggle-content>
        <slot></slot>
      </cr-card>
    `;
  }
}
